'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { vehicleImageUrl } from '@/lib/image';

interface Photo {
  id: string;
  storagePath: string;
  isPrimary: boolean;
  order: number;
}

interface PhotoGalleryProps {
  photos: Photo[];
  alt: string;
}

export function PhotoGallery({ photos, alt }: PhotoGalleryProps) {
  const sorted = [...photos].sort((a, b) => {
    if (a.isPrimary) return -1;
    if (b.isPrimary) return 1;
    return a.order - b.order;
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const prev = useCallback(() => {
    setActiveIndex((i) => (i === 0 ? sorted.length - 1 : i - 1));
  }, [sorted.length]);

  const next = useCallback(() => {
    setActiveIndex((i) => (i === sorted.length - 1 ? 0 : i + 1));
  }, [sorted.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, prev, next]);

  if (sorted.length === 0) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-xl bg-slate-100 text-slate-400">
        No photos available
      </div>
    );
  }

  const active = sorted[activeIndex];

  return (
    <>
      {/* Main photo */}
      <div
        className="relative aspect-video cursor-zoom-in overflow-hidden rounded-xl bg-slate-100"
        onClick={() => setLightboxOpen(true)}
      >
        <Image
          src={vehicleImageUrl(active.storagePath, 'detail')}
          alt={alt}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 896px"
          priority={activeIndex === 0}
        />
        {sorted.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur-sm hover:bg-black/60 transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur-sm hover:bg-black/60 transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2.5 py-1 text-xs text-white backdrop-blur-sm">
              {activeIndex + 1} / {sorted.length}
            </span>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {sorted.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {sorted.map((photo, i) => (
            <button
              key={photo.id}
              onClick={() => setActiveIndex(i)}
              className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                i === activeIndex
                  ? 'border-[var(--color-primary,#3B82F6)]'
                  : 'border-transparent hover:border-slate-300'
              }`}
            >
              <Image
                src={vehicleImageUrl(photo.storagePath, 'thumbnail')}
                alt={`${alt} — photo ${i + 1}`}
                fill
                className="object-cover"
                sizes="96px"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          {sorted.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous"
              >
                <ChevronLeft className="h-7 w-7" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next"
              >
                <ChevronRight className="h-7 w-7" />
              </button>
            </>
          )}

          <Image
            src={vehicleImageUrl(active.storagePath, 'lightbox')}
            alt={alt}
            width={2400}
            height={1600}
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
            sizes="90vw"
          />

          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/70">
            {activeIndex + 1} / {sorted.length}
          </span>
        </div>
      )}
    </>
  );
}
